import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { invalidatePaths, useApiMutation, useApiQuery } from "@/lib/query";
import type { Dataset } from "@/features/datasets/types";
import { experimentTally, scoringTally, type ScoringTally } from "./scoring/scoring-tally";
import { isLive } from "./status";
import type {
  ExperimentCreate,
  ExperimentFilter,
  ExperimentPatch,
  ExperimentSummary,
  ImportRequest,
  ItemDetail,
  PullRequest,
  RescoreRequest,
  ScoringRun,
  Size,
  SizeQuery,
  ToDatasetRequest,
  ToDatasetResult,
  TryCaseRequest,
  TryCaseResult,
} from "./types";

export { useExperimentItems } from "./items/use-live-items";

const POLL_MS = 2000;
const LISTS = [["/experiments"]];

/** The summary polls every 2 s while the experiment is moving, then stops. */
export const useExperiment = (id: string) =>
  useApiQuery<ExperimentSummary>(
    `/experiments/${id}`,
    {},
    {
      enabled: !!id,
      refetchInterval: (query) => (isLive(query.state.data?.status) ? POLL_MS : false),
    },
  );

/** Lists poll more gently, and only while something on them is running. */
export const useExperiments = (filter: ExperimentFilter = {}, enabled = true) =>
  useApiQuery<ExperimentSummary[]>(
    "/experiments",
    { ...filter },
    {
      enabled,
      refetchInterval: (query) => (query.state.data?.some((e) => isLive(e.status)) ? POLL_MS * 2 : false),
    },
  );

export const useItemDetail = (id: string, itemId: string | null) =>
  useApiQuery<ItemDetail>(`/experiments/${id}/items/${itemId}`, {}, { enabled: itemId !== null });

export const useExperimentSize = (query: SizeQuery | null) =>
  useApiQuery<Size>(
    "/experiments/size",
    query ? { ...query, evaluator_ids: query.evaluator_ids.join(",") } : {},
    { enabled: query !== null && query.evaluator_ids.length > 0 },
  );

/** Seed the detail cache with what the server returned, then refetch the lists. */
function useSettle() {
  const queryClient = useQueryClient();
  return (experiment: ExperimentSummary) => {
    queryClient.setQueryData([`/experiments/${experiment.id}`, {}], experiment);
    void invalidatePaths(queryClient, [...LISTS, `/experiments/${experiment.id}/items`, "/agents"]);
  };
}

export const useCreateExperiment = () =>
  useApiMutation<ExperimentSummary, ExperimentCreate>("post", "/experiments", { onSuccess: useSettle() });

/** Run evaluators on a period of a live dataset's traces (`collecting` until pulled). */
export const usePullExperiment = () =>
  useApiMutation<ExperimentSummary, PullRequest>("post", "/experiments/pull", { onSuccess: useSettle() });

/** "Try one case": a one-off answer, so it lives on the mutation, not in the cache. */
export const useTryCase = () => useApiMutation<TryCaseResult, TryCaseRequest>("post", "/experiments/try");

/** Save edits to a draft, and optionally start it straight after. */
export function useUpdateDraft(id: string) {
  const settle = useSettle();
  return useMutation({
    mutationFn: async ({ patch, start }: { patch: ExperimentPatch; start: boolean }) => {
      const saved = await api.patch<ExperimentSummary>(`/experiments/${id}`, patch);
      return start ? api.post<ExperimentSummary>(`/experiments/${id}/start`) : saved;
    },
    onSuccess: settle,
  });
}

export function useExperimentAction(id: string) {
  const settle = useSettle();
  return useMutation({
    mutationFn: (action: "start" | "stop" | "rerun-failed" | "rerun-all") =>
      action.startsWith("rerun")
        ? api.post<ExperimentSummary>(`/experiments/${id}/rerun`, {
            scope: action === "rerun-failed" ? "failed" : "all",
          })
        : api.post<ExperimentSummary>(`/experiments/${id}/${action}`),
    onSuccess: settle,
  });
}

const isActive = (runs: ScoringRun[] | undefined) =>
  !!runs?.some((r) => r.status === "queued" || r.status === "running");

/** Scoring runs poll while one is queued or running. When the last one finishes,
 *  the summary and items refetch so the new scores show. */
export function useScoringRuns(
  id: string,
  { poll = false, enabled = true }: { poll?: boolean; enabled?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const key = [`/experiments/${id}/scoring-runs`, {}];
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const before = queryClient.getQueryData<ScoringRun[]>(key);
      const runs = await api.get<ScoringRun[]>(`/experiments/${id}/scoring-runs`);
      if (isActive(before) && !isActive(runs)) {
        void invalidatePaths(queryClient, [
          [`/experiments/${id}`],
          `/experiments/${id}/items`,
          ...LISTS,
          "/compare",
        ]);
      }
      return runs;
    },
    enabled,
    refetchInterval: (query) => (poll || isActive(query.state.data) ? POLL_MS : false),
  });
}

/** The newest first scoring of every output, whatever its status. "Rerun failed" also
 *  makes an `initial` run, over the reset rows only, so it is skipped.
 *  `poll` keeps asking while the experiment is live, before the run exists. */
function useInitialScoringRun(id: string, poll: boolean, enabled: boolean): ScoringRun | null {
  const runs = useScoringRuns(id, { poll, enabled }).data;
  return runs?.find((r) => r.kind === "initial" && r.scope === "all") ?? null;
}

/** Where a trace run's scoring stands: the server's `scoring.progress`, else counted
 *  from its first scoring run. Agent runs have their own progress block: null. */
export function useScoringTally(e: ExperimentSummary): ScoringTally | null {
  const traceRun = e.runner === "dataset_output";
  const fromServer = traceRun ? experimentTally(e) : null;
  const run = useInitialScoringRun(e.id, isLive(e.status), traceRun && !fromServer);
  if (!traceRun) return null;
  return fromServer ?? (run ? scoringTally(run) : null);
}

/** The newest run still queued or running, if any. */
export function useActiveScoringRun(id: string): ScoringRun | null {
  const runs = useScoringRuns(id).data;
  return runs?.find((r) => r.status === "queued" || r.status === "running") ?? null;
}

/** New scores from stored outputs; the agent is not called. */
export const useRescore = () =>
  useApiMutation<ScoringRun, { experimentId: string; body: RescoreRequest }>(
    "post",
    (v) => `/experiments/${v.experimentId}/rescore`,
    { body: (v) => v.body, invalidate: ["/experiments", "/compare"] },
  );

/** Recorded outputs become an experiment (`runner: "dataset_output"`) and are scored. */
export const useImportRecorded = () =>
  useApiMutation<ExperimentSummary, ImportRequest>("post", "/experiments/import", {
    onSuccess: useSettle(),
  });

/** One dataset with its versions, or nothing until a dataset is picked. */
export const useDatasetVersions = (datasetId: string | null) =>
  useApiQuery<Dataset>(`/datasets/${datasetId}`, {}, { enabled: !!datasetId });

/** Trace results become dataset items; the dataset's versions and counts refetch. */
export const useAddToDataset = (experimentId: string) =>
  useApiMutation<ToDatasetResult, ToDatasetRequest>("post", `/experiments/${experimentId}/items/to-dataset`, {
    invalidate: ["/datasets"],
  });
